import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { SubcoLogo } from './SubcoBits';

const EMPTY = { name: '', logo_url: '', firm_ids: [], contact_name: '', email: '', phone: '', vat: '', address: '', note: '', active: true, owner_can_view_employees: true };
const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>;

export function SubcoCompanyDialog({ open, subco, firms, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (open) setForm(subco ? { ...EMPTY, ...Object.fromEntries(Object.entries(subco).map(([k, v]) => [k, v ?? EMPTY[k]])) } : EMPTY); }, [open, subco]);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const toggleFirm = (id) => setForm((p) => ({ ...p, firm_ids: p.firm_ids.includes(id) ? p.firm_ids.filter((x) => x !== id) : [...p.firm_ids, id] }));
  const pickLogo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 250 * 1024) return toast.error('Logo en fazla 250 KB olabilir');
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, logo_url: r.result }));
    r.readAsDataURL(f);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error('Subco adı zorunlu');
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v === '' ? null : v]));
      body.firm_ids = form.firm_ids;
      if (subco) await api.put(`/subco-companies/${subco.id}`, body);
      else await api.post('/subco-companies', body);
      toast.success(subco ? 'Subco güncellendi' : 'Subco eklendi');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] flex flex-col p-0" data-testid="subco-company-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle>{subco ? 'Subco Düzenle' : 'Yeni Subco Firması'}</DialogTitle>
          <DialogDescription>Taşeron firma bilgileri ve çalıştığı firmalar. Çalışanlar Kullanıcılar ekranından bu subco'ya bağlanır.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 px-6 py-4 overflow-y-auto">
          <div className="flex items-center gap-3">
            <SubcoLogo subco={form} size="h-14 w-14" text="text-lg" />
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="subco-company-logo-btn"><Upload className="h-3.5 w-3.5 mr-1" />Logo yükle</Button>
              {form.logo_url && <Button type="button" variant="ghost" size="sm" onClick={() => setForm((p) => ({ ...p, logo_url: '' }))} data-testid="subco-company-logo-clear"><X className="h-3.5 w-3.5" /></Button>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickLogo} />
            <p className="text-[10px] text-slate-400">Logo yoksa baş harfler gösterilir.</p>
          </div>
          <F label="Subco adı *"><Input value={form.name} onChange={set('name')} placeholder="Örn: Barış TRANS" data-testid="subco-company-name" /></F>
          <div className="grid grid-cols-2 gap-2">
            <F label="Yetkili ad soyad"><Input value={form.contact_name} onChange={set('contact_name')} data-testid="subco-company-contact" /></F>
            <F label="Telefon"><Input value={form.phone} onChange={set('phone')} data-testid="subco-company-phone" /></F>
            <F label="E-posta"><Input type="email" value={form.email} onChange={set('email')} data-testid="subco-company-email" /></F>
            <F label="KDV / BTW no"><Input value={form.vat} onChange={set('vat')} data-testid="subco-company-vat" /></F>
          </div>
          <F label="Adres"><Input value={form.address} onChange={set('address')} data-testid="subco-company-address" /></F>
          <F label={`Çalıştığı firmalar${form.firm_ids.length ? ` — ${form.firm_ids.length} seçili` : ''}`}>
            <div className="border rounded p-2 max-h-40 overflow-auto space-y-1" data-testid="subco-company-firms">
              {firms.map((f) => (
                <label key={f.id} className="flex items-center gap-2 text-sm hover:bg-amber-50 px-1 py-0.5 rounded cursor-pointer">
                  <input type="checkbox" checked={form.firm_ids.includes(f.id)} onChange={() => toggleFirm(f.id)} data-testid={`subco-company-firm-${f.id}`} />{f.name}
                </label>
              ))}
            </div>
          </F>
          <F label="Not"><Input value={form.note} onChange={set('note')} /></F>
          <label className="flex items-center justify-between gap-3 rounded-lg border p-2.5">
            <span><span className="block text-sm font-semibold">Yetkili kendi çalışanlarını görebilir</span><span className="block text-[11px] text-slate-500">Subco giriş hesabı "Çalışanlarım" sayfasında listeyi görür (düzenleyemez)</span></span>
            <Switch checked={form.owner_can_view_employees} onCheckedChange={set('owner_can_view_employees')} data-testid="subco-company-owner-view" />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-lg border p-2.5">
            <span className="text-sm font-semibold">Aktif</span>
            <Switch checked={form.active} onCheckedChange={set('active')} data-testid="subco-company-active" />
          </label>
        </div>
        <DialogFooter className="px-6 py-3 border-t">
          <Button variant="ghost" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={busy} data-testid="subco-company-save">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
